import { reactive, readonly } from 'vue';

// Everything this app persists about the signed in user. Kept in one place so signing out
// (from the profile menu, or because the API rejected our token) always clears the same set.
export const SESSION_STORAGE_KEYS = [
  'token',
  'userId',
  'userName',
  'isStaff',
  'firstName',
  'lastName',
  'staffPosition',
  'counterId',
  'counterGroupId',
];

// Values that are numbers everywhere except in storage, which only holds strings.
const NUMERIC_KEYS = ['userId', 'counterId', 'counterGroupId'];

// localStorage stringifies whatever it is given, so a missing value written as `null`
// comes back as the four characters "null" and every reader has to know to compare
// against that. Normalise it back to a real null once, here.
function parse(key, raw) {
  if (raw === null || raw === '' || raw === 'null' || raw === 'undefined') {
    return null;
  }
  if (NUMERIC_KEYS.includes(key)) {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (key === 'isStaff') {
    return raw === 'true';
  }
  return raw;
}

function readAll() {
  const next = {};
  SESSION_STORAGE_KEYS.forEach((key) => {
    next[key] = parse(key, localStorage.getItem(key));
  });
  return next;
}

// The signed-in user, as reactive state. localStorage stays the place the session
// survives a reload, but nothing may read it directly for display: Vue cannot see a
// localStorage write, so a computed built on one keeps returning the value it had when
// it was first evaluated. That is what left the previous user's name and position on
// screen after signing in as someone else - the markup was correct, it was just never
// told anything had changed. Read through this object instead and every dependent
// computed re-runs on its own.
const state = reactive(readAll());

export const session = readonly(state);

export function getToken() {
  return state.token;
}

export function isSignedIn() {
  return Boolean(state.token);
}

// The single way a session is written. Keeping the write here is what guarantees the
// reactive copy above can never drift from what is in storage.
export function setStoredSession(values) {
  SESSION_STORAGE_KEYS.forEach((key) => {
    const value = values[key];
    if (value === null || value === undefined || value === '' || value === 'null') {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
    state[key] = parse(key, localStorage.getItem(key));
  });
}

export function clearStoredSession() {
  SESSION_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    state[key] = null;
  });
}
